const selectors = {
  nav: '[data-faq-nav]',
  indicator: '[data-faq-indicator]',
};

const classes = {
  indicatorActive: 'bg-primary',
  indicatorInactive: 'bg-transparent',
  navActive: 'font-bold',
};

// Fraction of the viewport height a group heading must pass before it counts as active.
const SCROLL_OFFSET = 0.25;

// Slack for sub-pixel scroll heights when testing whether the page is fully scrolled.
const BOTTOM_TOLERANCE = 2;

export default class MainFaq {
  constructor(section) {
    this.el = section.el;
    this.id = section.id;

    this.activeId = null;
    this.groups = [];

    this._init();
  }

  _init() {
    const navItems = Array.from(this.el.querySelectorAll(selectors.nav));

    if (navItems.length === 0) {
      return;
    }

    this.groups = navItems
      .map(nav => ({
        nav,
        indicator: nav.querySelector(selectors.indicator),
        target: this.el.querySelector(`#${CSS.escape(nav.dataset.faqNav)}`),
      }))
      .filter(group => group.target && group.indicator);

    if (this.groups.length === 0) {
      return;
    }

    // The observer only decides *when* to recompute; _updateActive decides which group
    // wins, so a heading entering or leaving the band is enough to re-evaluate.
    this.observer = new IntersectionObserver(this._onIntersect, {
      rootMargin: '-10% 0px -70% 0px',
      threshold: 0,
    });

    this.groups.forEach(({ target }) => this.observer.observe(target));
    this._updateActive();
  }

  _onIntersect = () => {
    this._updateActive();
  };

  _updateActive() {
    const offset = window.innerHeight * SCROLL_OFFSET;
    let active = this.groups[0];

    for (const group of this.groups) {
      if (group.target.getBoundingClientRect().top <= offset) {
        active = group;
      }
    }

    // The last group is often too short to ever clear the offset, so pin it at the
    // bottom of the page rather than leaving the previous group highlighted.
    const nearBottom =
      window.innerHeight + window.scrollY >=
      document.documentElement.scrollHeight - BOTTOM_TOLERANCE;

    if (nearBottom) {
      active = this.groups[this.groups.length - 1];
    }

    this._setActive(active.target.id);
  }

  _setActive(id) {
    if (this.activeId === id) {
      return;
    }

    this.activeId = id;

    this.groups.forEach(({ nav, indicator, target }) => {
      const isActive = target.id === id;

      indicator.classList.toggle(classes.indicatorActive, isActive);
      indicator.classList.toggle(classes.indicatorInactive, !isActive);
      nav.classList.toggle(classes.navActive, isActive);
    });
  }

  onSectionUnload = () => {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  };
}
